/* =====================================================================
   JMRE Garage — interacciones de la landing
   ---------------------------------------------------------------------
   - Cada bloque está aislado y protegido: si la sección/elementos no
     existen, simplemente no hace nada. Seguro de incluir en cualquier
     página sin generar errores.
   - Compatible con secciones fraccionadas en Shopify: cada componente
     se busca por data-attribute y se inicializa de forma independiente.
   ===================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     1) TABS
     Estructura esperada (todo opcional — si no aparece, no pasa nada):
       <... data-tab-group>
         <... data-tabs>
           <button data-tab="slug">...</button>
         </...>
         <... data-panel="slug">...</...>
       </...>
     ------------------------------------------------------------------ */
  function initTabs(group) {
    if (!group) return;
    var tabsContainer = group.querySelector('[data-tabs]');
    if (!tabsContainer) return;

    var tabs = tabsContainer.querySelectorAll('[data-tab]');
    if (!tabs.length) return;

    function activate(slug) {
      tabs.forEach(function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-tab') === slug);
      });
      group.querySelectorAll('[data-panel]').forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-panel') === slug);
      });
      // Notifica a sliders dentro del nuevo panel para refrescar estado.
      var active = group.querySelector('[data-panel].is-active');
      if (active) {
        active.querySelectorAll('[data-slider]').forEach(function (s) {
          if (typeof s._refresh === 'function') s._refresh();
        });
      }
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        var slug = t.getAttribute('data-tab');
        if (slug) activate(slug);
      });
    });
  }

  /* ------------------------------------------------------------------
     2) SLIDER de productos
     Estructura esperada:
       <... data-slider>
         <... class="slider__track">
           <article>...</article> (cards)
         </...>
         <... data-slider-nav>
           <button data-prev>...</button>
           <button data-next>...</button>
         </...>
       </...>
     ------------------------------------------------------------------ */
  function initSlider(slider) {
    if (!slider) return;
    var track = slider.querySelector('.slider__track');
    if (!track) return;

    var prevBtn = slider.querySelector('[data-prev]');
    var nextBtn = slider.querySelector('[data-next]');

    /* ---------- Infinite loop: clonamos los items y al cruzar el ancho original
       saltamos silenciosamente para crear la sensación de loop continuo. ---------- */
    var loopEnabled = slider.getAttribute('data-loop') === 'true';
    var originalWidth = 0;
    var originalCount = track.children.length;

    if (loopEnabled && originalCount > 1) {
      // Mide ancho real de los items originales antes de clonar
      originalWidth = track.scrollWidth;
      var originals = Array.prototype.slice.call(track.children);
      originals.forEach(function (el) {
        var clone = el.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-cloned', 'true');
        // Si el clone tiene IDs/handles, marca para evitar duplicados de behavior si fuera necesario
        track.appendChild(clone);
      });
    }

    function step() {
      var card = track.querySelector('.product-card') || track.firstElementChild;
      if (!card) return track.clientWidth * 0.8;
      var cardW = card.getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return cardW + gap;
    }

    function maxScroll() { return track.scrollWidth - track.clientWidth - 1; }

    function maybeWrap() {
      if (!loopEnabled || originalWidth === 0) return;
      // Si pasamos el ancho original, saltamos atrás silenciosamente
      if (track.scrollLeft >= originalWidth) {
        var prevBehavior = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = track.scrollLeft - originalWidth;
        // Forzar reflow antes de restaurar smooth para evitar animar el jump
        void track.offsetWidth;
        track.style.scrollBehavior = prevBehavior || '';
      }
    }

    /* Debounced: el wrap se ejecuta solo cuando el scroll se detiene
       (después de que termina el smooth scroll). Así no interrumpe la
       animación a mitad de camino. */
    var scrollIdleTimer = null;
    function debouncedMaybeWrap() {
      if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
      scrollIdleTimer = setTimeout(maybeWrap, 80);
    }

    function go(dir) {
      track.scrollBy({ left: dir * step(), behavior: 'smooth' });
    }

    function updateNav() {
      if (!prevBtn && !nextBtn) return;
      var max = maxScroll();
      if (max <= 0) {
        if (prevBtn) prevBtn.disabled = true;
        if (nextBtn) nextBtn.disabled = true;
        return;
      }
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= max;
    }

    if (prevBtn) prevBtn.addEventListener('click', function () {
      pauseAutoplay(); go(-1);
    });
    if (nextBtn) nextBtn.addEventListener('click', function () {
      pauseAutoplay(); go(1);
    });

    track.addEventListener('scroll', function () {
      updateNav();
      debouncedMaybeWrap();
    }, { passive: true });
    window.addEventListener('resize', updateNav);

    /* ---------- Autoplay ---------- */
    var autoplayEnabled = slider.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(slider.getAttribute('data-autoplay-interval'), 10) || 5000;
    var timer = null;
    var pausedByUser = false;
    var resumeAfterPauseMs = 8000;
    var resumeTimer = null;

    function autoplayStep() {
      var max = maxScroll();
      if (max <= 0) return; // nada que deslizar
      if (loopEnabled && originalWidth > 0) {
        // En loop infinito: siempre avanzamos. maybeWrap() en el scroll handler
        // se encarga del salto silencioso al cruzar originalWidth.
        go(1);
      } else if (track.scrollLeft >= max - 4) {
        // Sin loop: al final volvemos al inicio con scroll suave
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        go(1);
      }
    }

    function startAutoplay() {
      if (!autoplayEnabled || pausedByUser) return;
      stopAutoplay();
      timer = setInterval(autoplayStep, interval);
    }

    function stopAutoplay() {
      if (timer) { clearInterval(timer); timer = null; }
    }

    function pauseAutoplay() {
      pausedByUser = true;
      stopAutoplay();
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        pausedByUser = false;
        startAutoplay();
      }, resumeAfterPauseMs);
    }

    if (autoplayEnabled) {
      slider.addEventListener('mouseenter', stopAutoplay);
      slider.addEventListener('mouseleave', function () {
        if (!pausedByUser) startAutoplay();
      });
      track.addEventListener('touchstart', pauseAutoplay, { passive: true });
      // Pausar si la pestaña está oculta para no consumir CPU
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stopAutoplay();
        else if (!pausedByUser) startAutoplay();
      });
      startAutoplay();
    }

    // Exponemos un refresh para que los tabs lo llamen al cambiar de panel.
    slider._refresh = function () {
      requestAnimationFrame(function () {
        updateNav();
        if (autoplayEnabled && !pausedByUser) startAutoplay();
      });
    };

    updateNav();
  }

  /* ------------------------------------------------------------------
     3) NAV — menu off-canvas en mobile + dropdowns en desktop/mobile
     Estructura esperada (todo opcional):
       <button data-nav-toggle>...</button>
       <nav data-nav>
         <button data-nav-close>...</button>
         <div data-dropdown>
           <button data-dropdown-toggle>...</button>
           <div class="nav__dropdown">...</div>
         </div>
         ...
       </nav>
       <div data-nav-backdrop></div>
     ------------------------------------------------------------------ */
  function initNav() {
    var nav      = document.querySelector('[data-nav]');
    var toggle   = document.querySelector('[data-nav-toggle]');
    var closeBtn = document.querySelector('[data-nav-close]');
    var backdrop = document.querySelector('[data-nav-backdrop]');
    var dropdowns = document.querySelectorAll('[data-dropdown]');

    function setOpen(open) {
      if (nav)      nav.classList.toggle('is-open', open);
      if (toggle)   { toggle.classList.toggle('is-open', open); toggle.setAttribute('aria-expanded', open ? 'true' : 'false'); }
      if (backdrop) backdrop.classList.toggle('is-open', open);
      document.body.style.overflow = open ? 'hidden' : '';
    }

    if (toggle && nav) {
      toggle.addEventListener('click', function () {
        setOpen(!nav.classList.contains('is-open'));
      });
    }
    if (closeBtn) closeBtn.addEventListener('click', function () { setOpen(false); });
    if (backdrop) backdrop.addEventListener('click', function () { setOpen(false); });

    // Cierra el menu al hacer clic en un enlace simple (no en el trigger de dropdown)
    if (nav) {
      nav.querySelectorAll('a.nav__link').forEach(function (a) {
        a.addEventListener('click', function () { setOpen(false); });
      });
    }

    // Dropdowns
    function closeAllDropdowns(except) {
      dropdowns.forEach(function (d) {
        if (d !== except) {
          d.classList.remove('is-open');
          var t = d.querySelector('[data-dropdown-toggle]');
          if (t) t.setAttribute('aria-expanded', 'false');
        }
      });
    }

    dropdowns.forEach(function (dd) {
      var trigger = dd.querySelector('[data-dropdown-toggle]');
      if (!trigger) return;
      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var nowOpen = !dd.classList.contains('is-open');
        closeAllDropdowns(dd);
        dd.classList.toggle('is-open', nowOpen);
        trigger.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      });
    });

    // Click fuera cierra dropdowns en desktop
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-dropdown]')) closeAllDropdowns(null);
    });

    // Esc cierra todo
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeAllDropdowns(null);
        setOpen(false);
      }
    });

    // Al redimensionar a desktop, asegura que el body se desbloquee
    window.addEventListener('resize', function () {
      if (window.innerWidth > 992 && nav && nav.classList.contains('is-open')) {
        setOpen(false);
      }
    });
  }

  /* ------------------------------------------------------------------
     4) WISHLIST — persiste en localStorage; sincroniza visualmente todos
        los botones con el mismo product handle en la página.
     Estructura esperada:
       <button data-wishlist-toggle
               data-product-handle="..."
               data-product-id="..."
               data-product-title="...">…</button>
     Eventos:
       window 'jmre:wishlist:changed' (CustomEvent con detail.items)
     ------------------------------------------------------------------ */
  var WISHLIST_KEY = 'jmre_wishlist';

  function wlGet() {
    try {
      var raw = localStorage.getItem(WISHLIST_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  }

  function wlSet(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); } catch (e) {}
    window.dispatchEvent(new CustomEvent('jmre:wishlist:changed', { detail: { items: list } }));
  }

  function wlHas(handle) {
    if (!handle) return false;
    return wlGet().some(function (it) { return it.handle === handle; });
  }

  function wlToggle(item) {
    if (!item || !item.handle) return false;
    var list = wlGet();
    var idx = list.findIndex(function (it) { return it.handle === item.handle; });
    if (idx === -1) {
      list.push(item);
      wlSet(list);
      return true;
    } else {
      list.splice(idx, 1);
      wlSet(list);
      return false;
    }
  }

  function syncWishlistButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      var active = wlHas(handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.setAttribute(
        'aria-label',
        (active ? 'Remove ' : 'Add ') + (btn.getAttribute('data-product-title') || 'product') + ' to wishlist'
      );
    });

    // Counter badge en el header (y cualquier otro [data-wishlist-count])
    var count = wlGet().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  function initWishlist() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;

      wlToggle({
        handle: handle,
        id: btn.getAttribute('data-product-id') || null,
        title: btn.getAttribute('data-product-title') || '',
        added_at: Date.now(),
      });

      syncWishlistButtons();
    });

    syncWishlistButtons();
    window.addEventListener('storage', function (e) {
      if (e.key === WISHLIST_KEY) syncWishlistButtons();
    });
    window.addEventListener('jmre:wishlist:changed', syncWishlistButtons);
  }

  // API pública (header counter, /pages/wishlist, etc.)
  window.JMRE = window.JMRE || {};
  window.JMRE.wishlist = {
    get: wlGet,
    has: wlHas,
    toggle: wlToggle,
    clear: function () { wlSet([]); },
    count: function () { return wlGet().length; },
  };

  /* ------------------------------------------------------------------
     5) WISHLIST DRAWER — popup lateral con productos + add to cart.
     Estructura esperada:
       <a data-wishlist-drawer-toggle>...</a>
       <aside data-wishlist-drawer>
         <div data-wishlist-list></div>
       </aside>
       <div data-wishlist-drawer-close></div>  (backdrop)
     ------------------------------------------------------------------ */
  var productCache = {};

  function fmtMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    return 'US $' + dollars;
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (p) { productCache[handle] = p; return p; });
  }

  function renderDrawerItem(item, product) {
    var available = product && product.available;
    var firstVariant = product && product.variants && product.variants[0];
    var img = product && product.featured_image
      ? product.featured_image
      : (item.image || '');

    var addBtn;
    if (!available || !firstVariant) {
      addBtn = '<button class="wishlist-drawer__add" disabled>Sold out</button>';
    } else if (product.variants.length > 1) {
      addBtn = '<a class="wishlist-drawer__add" href="' + product.url + '">View options</a>';
    } else {
      addBtn = '<button class="wishlist-drawer__add" data-wishlist-add-to-cart data-variant-id="'
        + firstVariant.id + '" data-product-title="' + (product.title || '') + '">Add to cart</button>';
    }

    var price = product ? fmtMoney(product.price) : '';

    return ''
      + '<div class="wishlist-drawer__item" data-handle="' + item.handle + '">'
      + '  <a class="wishlist-drawer__thumb" href="' + (product ? product.url : '#') + '">'
      + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '')
      + '  </a>'
      + '  <div class="wishlist-drawer__info">'
      + '    <a class="wishlist-drawer__title" href="' + (product ? product.url : '#') + '">' + (product ? product.title : item.title || item.handle) + '</a>'
      + '    <span class="wishlist-drawer__price">' + price + '</span>'
      + '  </div>'
      + '  <div class="wishlist-drawer__actions">'
      +      addBtn
      + '    <button class="wishlist-drawer__remove" data-wishlist-remove data-handle="' + item.handle + '">Remove</button>'
      + '  </div>'
      + '</div>';
  }

  function renderDrawer() {
    var list = document.querySelector('[data-wishlist-list]');
    if (!list) return;
    var items = wlGet();

    if (items.length === 0) {
      list.innerHTML = '<p class="wishlist-drawer__empty">Your wishlist is empty. Click on the heart icon on any product to add it.</p>';
      return;
    }

    // Render placeholder mientras carga
    list.innerHTML = items.map(function (it) {
      return renderDrawerItem(it, null);
    }).join('');

    // Hidrata con datos reales
    items.forEach(function (it) {
      fetchProduct(it.handle)
        .then(function (product) {
          var current = list.querySelector('[data-handle="' + it.handle + '"]');
          if (current) current.outerHTML = renderDrawerItem(it, product);
        })
        .catch(function () {
          // si falla la fetch, dejamos el placeholder
        });
    });
  }

  function openDrawer() {
    var drawer = document.querySelector('[data-wishlist-drawer]');
    var backdrop = document.querySelector('.wishlist-drawer-backdrop');
    if (!drawer) return;
    renderDrawer();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (backdrop) {
      backdrop.removeAttribute('hidden');
      requestAnimationFrame(function () { backdrop.classList.add('is-open'); });
    }
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    var drawer = document.querySelector('[data-wishlist-drawer]');
    var backdrop = document.querySelector('.wishlist-drawer-backdrop');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    if (backdrop) {
      backdrop.classList.remove('is-open');
      setTimeout(function () { backdrop.setAttribute('hidden', ''); }, 250);
    }
    document.body.style.overflow = '';
  }

  function addToCart(variantId, btn) {
    if (!variantId) return;
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = '...';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart add failed');
        return r.json();
      })
      .then(function () {
        btn.textContent = '✓ Added to cart';
        btn.classList.add('is-added');
        // Notifica para refrescar contador del carrito en el header
        window.dispatchEvent(new CustomEvent('jmre:cart:added', { detail: { variantId: variantId } }));
        setTimeout(function () {
          btn.textContent = originalText;
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 1800);
      })
      .catch(function () {
        btn.textContent = 'Error';
        setTimeout(function () {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      });
  }

  function initWishlistDrawer() {
    // Toggle (icono del header)
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-wishlist-drawer-toggle]');
      if (trigger) {
        e.preventDefault();
        openDrawer();
        return;
      }
      var closer = e.target.closest('[data-wishlist-drawer-close]');
      if (closer) {
        e.preventDefault();
        closeDrawer();
        return;
      }
      var addBtn = e.target.closest('[data-wishlist-add-to-cart]');
      if (addBtn) {
        e.preventDefault();
        addToCart(addBtn.getAttribute('data-variant-id'), addBtn);
        return;
      }
      var removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        var handle = removeBtn.getAttribute('data-handle');
        if (handle) {
          wlToggle({ handle: handle });
          renderDrawer();
          syncWishlistButtons();
        }
      }
    });

    // Esc cierra el drawer
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    // Re-render del drawer cuando cambia el wishlist (cross-tab incluido)
    window.addEventListener('jmre:wishlist:changed', function () {
      var drawer = document.querySelector('[data-wishlist-drawer]');
      if (drawer && drawer.classList.contains('is-open')) renderDrawer();
    });
  }

  /* ------------------------------------------------------------------
     6) CART DRAWER — popup lateral con líneas del carrito + cambios.
     Estructura esperada:
       <a data-cart-drawer-toggle>...</a>
       <aside data-cart-drawer>
         <div data-cart-list></div>
         <footer data-cart-foot>
           <span data-cart-subtotal></span>
         </footer>
       </aside>
       <div data-cart-drawer-close></div>
     ------------------------------------------------------------------ */
  function cartFmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function syncCartCount(cart) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  function renderCartItem(item) {
    var img = item.image || (item.featured_image && item.featured_image.url) || '';
    var variantLabel = item.variant_title && item.variant_title !== 'Default Title'
      ? '<span class="cart-drawer__variant">' + item.variant_title + '</span>'
      : '';
    return ''
      + '<div class="cart-drawer__item" data-line="' + item.line + '" data-key="' + item.key + '">'
      + '  <a class="cart-drawer__thumb" href="' + item.url + '">'
      + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '')
      + '  </a>'
      + '  <div class="cart-drawer__info">'
      + '    <a class="cart-drawer__title" href="' + item.url + '">' + item.product_title + '</a>'
      +      variantLabel
      + '    <div class="cart-drawer__qty">'
      + '      <button type="button" data-cart-qty-decrease aria-label="Decrease">−</button>'
      + '      <span>' + item.quantity + '</span>'
      + '      <button type="button" data-cart-qty-increase aria-label="Increase">+</button>'
      + '    </div>'
      + '  </div>'
      + '  <div class="cart-drawer__line-actions">'
      + '    <span class="cart-drawer__line-price">' + cartFmtMoney(item.final_line_price) + '</span>'
      + '    <button type="button" class="cart-drawer__line-remove" data-cart-line-remove>Remove</button>'
      + '  </div>'
      + '</div>';
  }

  function renderCartDrawer(cart) {
    var list = document.querySelector('[data-cart-list]');
    var foot = document.querySelector('[data-cart-foot]');
    var subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (!list) return;

    if (!cart || cart.item_count === 0) {
      list.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      if (foot) foot.setAttribute('hidden', '');
      return;
    }

    // Shopify devuelve items con índice 0-based pero /cart/change.js usa 1-based.
    cart.items.forEach(function (it, i) { it.line = i + 1; });

    list.innerHTML = cart.items.map(renderCartItem).join('');
    if (subtotalEl) subtotalEl.textContent = cartFmtMoney(cart.items_subtotal_price || cart.total_price);
    if (foot) foot.removeAttribute('hidden');
  }

  function refreshCart(openAfter) {
    return fetchCart().then(function (cart) {
      syncCartCount(cart);
      renderCartDrawer(cart);
      if (openAfter) openCartDrawer(true /* skipFetch */);
      return cart;
    });
  }

  function openCartDrawer(skipFetch) {
    var drawer = document.querySelector('[data-cart-drawer]');
    var backdrop = document.querySelector('.cart-drawer-backdrop');
    if (!drawer) return;
    if (!skipFetch) refreshCart(false);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (backdrop) {
      backdrop.removeAttribute('hidden');
      requestAnimationFrame(function () { backdrop.classList.add('is-open'); });
    }
    document.body.style.overflow = 'hidden';
  }

  function closeCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    var backdrop = document.querySelector('.cart-drawer-backdrop');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    if (backdrop) {
      backdrop.classList.remove('is-open');
      setTimeout(function () { backdrop.setAttribute('hidden', ''); }, 250);
    }
    document.body.style.overflow = '';
  }

  function changeLineQty(line, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        syncCartCount(cart);
        renderCartDrawer(cart);
        return cart;
      });
  }

  function initCartDrawer() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cart-drawer-toggle]');
      if (trigger) {
        e.preventDefault();
        openCartDrawer();
        return;
      }
      var closer = e.target.closest('[data-cart-drawer-close]');
      if (closer) {
        e.preventDefault();
        closeCartDrawer();
        return;
      }
      var item = e.target.closest('[data-cart-drawer] [data-line]');
      if (!item) return;

      var line = parseInt(item.getAttribute('data-line'), 10);
      var qtyEl = item.querySelector('.cart-drawer__qty span');
      var current = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;

      if (e.target.closest('[data-cart-qty-increase]')) {
        e.preventDefault();
        changeLineQty(line, current + 1);
      } else if (e.target.closest('[data-cart-qty-decrease]')) {
        e.preventDefault();
        changeLineQty(line, Math.max(0, current - 1));
      } else if (e.target.closest('[data-cart-line-remove]')) {
        e.preventDefault();
        changeLineQty(line, 0);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeCartDrawer();
    });

    // Cuando alguien agrega al carrito (incl. desde el wishlist drawer),
    // refrescamos contador y abrimos el drawer.
    window.addEventListener('jmre:cart:added', function () {
      refreshCart(true);
    });

    // Sync inicial del contador (por si hay items pre-existentes y la
    // página se cargó sin el valor exacto).
    fetchCart().then(syncCartCount).catch(function () {});
  }

  // API pública
  window.JMRE.cart = {
    refresh: refreshCart,
    open: openCartDrawer,
    close: closeCartDrawer,
  };

  /* ------------------------------------------------------------------
     Boot — inicializa todo lo que encuentre en el documento.
     Si en el futuro se fracciona en secciones de Shopify, basta con
     incluir este archivo una sola vez en el theme.
     ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     7) SEARCH POPUP — abre un panel con un form de búsqueda en mobile.
     Estructura esperada:
       <button data-search-popup-toggle>...</button>
       <div data-search-popup>
         <input data-search-popup-input>
       </div>
       <div data-search-popup-close></div>  (backdrop)
     ------------------------------------------------------------------ */
  function initSearchPopup() {
    var popup = document.querySelector('[data-search-popup]');
    var backdrop = document.querySelector('.search-popup-backdrop');
    var input = document.querySelector('[data-search-popup-input]');
    if (!popup) return;

    function openPopup() {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      if (backdrop) {
        backdrop.removeAttribute('hidden');
        requestAnimationFrame(function () { backdrop.classList.add('is-open'); });
      }
      document.body.style.overflow = 'hidden';
      // Foco al input (con delay pequeño para que la transición no lo robe)
      setTimeout(function () { if (input) input.focus(); }, 200);
    }

    function closePopup() {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      if (backdrop) {
        backdrop.classList.remove('is-open');
        setTimeout(function () { backdrop.setAttribute('hidden', ''); }, 250);
      }
      document.body.style.overflow = '';
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-popup-toggle]')) {
        e.preventDefault();
        openPopup();
        return;
      }
      if (e.target.closest('[data-search-popup-close]')) {
        e.preventDefault();
        closePopup();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
    });
  }

  /* ------------------------------------------------------------------
     8) ADD TO CART desde las product cards ([data-add-to-cart])
     POST /cart/add.js y dispara jmre:cart:added, que ya refresca el
     contador y abre el cart drawer (ver initCartDrawer).
     ------------------------------------------------------------------ */
  function initCardAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      var original = btn.innerHTML;
      var label = btn.querySelector('.product-card__cart-label');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Cart add failed');
          return r.json();
        })
        .then(function () {
          btn.classList.add('is-added');
          if (label) label.textContent = 'Added ✓';
          window.dispatchEvent(new CustomEvent('jmre:cart:added', { detail: { variantId: variantId } }));
        })
        .catch(function () {
          if (label) label.textContent = 'Error';
        })
        .then(function () {
          setTimeout(function () {
            btn.innerHTML = original;
            btn.classList.remove('is-added');
            btn.removeAttribute('aria-busy');
            btn.disabled = false;
          }, 1600);
        });
    });
  }

  function boot() {
    document.querySelectorAll('[data-tab-group]').forEach(initTabs);
    document.querySelectorAll('[data-slider]').forEach(initSlider);
    initNav();
    initWishlist();
    initWishlistDrawer();
    initCartDrawer();
    initCardAddToCart();
    initSearchPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
